<script lang="ts">
  import { goto } from '$app/navigation';

  import { inview } from 'svelte-inview';

  import { ArtAssetType, AuctionStatus } from '@do/models';
  import { DirectusImage, Preset } from '@do/svelte-directus-images';
  import { useWallet } from '@do/svelte-web3-manager';

  import { pointer } from '@shared/libs/tools';
  import { Icon } from '@shared/ui/icon';
  import { TextUnderline } from '@shared/ui/text-underline';
  import { seamlessImageNavigation } from '@shared/ui/transition-animator/helpers';
  import { cn, is, truncateString } from '@shared/utils';
  import { getPrice } from '@shared/utils/blockchain';

  import {
    artAssetCardLibs,
    TRANSITION_DELAY_BETWEEN_IMAGE_AND_FIRST_LINE,
    TRANSITION_DELAY_BETWEEN_LINES,
  } from '@entities/art-asset';
  import type { Auctions } from '@entities/auction';
  import { CuratorBadge } from '@entities/curator';
  import { walletLibs } from '@entities/wallet';

  import type { AuctionCardArtAsset } from '../types';
  import ActiveState from './auction-card-active.svelte';
  import FinalizeState from './auction-card-finalize-state.svelte';
  import SoldState from './auction-card-sold-state.svelte';

  export let id: string;
  export let artAsset: AuctionCardArtAsset;
  export let status: AuctionStatus;
  export let startDate: Date | null | undefined;
  export let duration: number;
  export let bestBid: Auctions['data'][number]['best_bid'];
  export let initialPrice: number | null = null;
  export let hasHover = true;
  export let containerClass = '';
  export let imageWrapperClass = '';
  export let isImageBlurred = false;

  const wallet = useWallet();

  let wasInview = false;
  let isImageLoaded = false;
  let imgElement: HTMLImageElement;

  $: isActive = status === AuctionStatus.Active && new Date() < endDate;
  $: endDate = new Date(new Date(String(startDate)).getTime() + duration);
  $: normalizedBestBid = bestBid?.bidder_wallet ? { value: bestBid?.value, wallet: bestBid?.bidder_wallet } : null;
  $: isUserBestBidder = walletLibs.checkIsBestBidder(normalizedBestBid, $wallet?.address);
  $: price = getPrice(bestBid?.value_formatted || initialPrice || 0);

  $: isVisible = (isImageLoaded || imgElement?.complete) && wasInview;

  //animations
  const options = { rootMargin: '0px', threshold: 0.1 };

  const appearanceDelay = artAssetCardLibs.imageIdToNumber(artAsset.mainPhoto?.id); // get up to 3 digit value from image id. Additional check cuz we have cases where image is undefined

  $: opacityTransitionClasses = `transition-opacity ${is(isVisible, 'opacity-1', 'opacity-0')} duration-500`;

  const getTransitionDelay = (order: number): string =>
    `${appearanceDelay + TRANSITION_DELAY_BETWEEN_IMAGE_AND_FIRST_LINE + TRANSITION_DELAY_BETWEEN_LINES * order}ms`;

  function handleEnter() {
    wasInview = true;
  }

  function handleImageLoad() {
    isImageLoaded = true;
  }
</script>

<a
  href="/auction-{id}/"
  draggable="false"
  on:click="{(e) => {
    // TODO: Improve condition to check artAsset type
    if (artAsset.digitalOriginal) {
      seamlessImageNavigation(e, imgElement, isImageBlurred);
    } else {
      goto(`/auction-${id}/`);
    }
  }}"
  use:inview="{options}"
  on:inview_enter="{handleEnter}"
>
  <div
    class="{cn(
      'group relative w-full break-inside-avoid transition-transform duration-500',
      {
        'hover:scale-[102%]': hasHover,
      },
      containerClass,
    )}"
  >
    <div
      class="{is(
        isVisible,
        'opacity-1 translate-y-0 scale-100',
        'translate-y-[80%] scale-75 opacity-0',
      )} mb-4 overflow-hidden [transition:opacity_0.5s,transform_0.8s]"
      style="transition-delay: {appearanceDelay}ms;"
    >
      <div
        class="{cn(
          'relative w-full cursor-pointer [transition:transform_0.8s]',
          {
            'translate-y-0': isVisible,
            '-translate-y-[60%]': !isVisible,
            'blur-xl': isImageBlurred,
          },
          imageWrapperClass,
        )}"
        use:pointer="{'view'}"
        style="transition-delay: {appearanceDelay}ms;"
      >
        <DirectusImage
          imageData="{artAsset.mainPhoto}"
          alt="{artAsset.name}"
          imgClasses="w-full h-full object-contain"
          pictureSizes="{{
            default: 'calc(100vw - 40px)',
            'min-768': '42vw',
            'min-1280': '34vw',
            'min-1500': '500px',
          }}"
          presetKeys="{Preset.Thumbnail}"
          on:load="{handleImageLoad}"
          bind:imgElement
        />
      </div>
    </div>

    <div class="flex justify-between gap-2">
      <div>
        {#if artAsset.artistsNames}
          <div
            class="{opacityTransitionClasses} flex items-center gap-3"
            style="transition-delay: {getTransitionDelay(1)}"
          >
            <TextUnderline classes="font-bold">
              {artAsset.artistsNames}
            </TextUnderline>
          </div>
        {/if}

        {#if status !== AuctionStatus.Sold && artAsset.digitalOriginal}
          <p
            class="{opacityTransitionClasses} mb-3"
            style="transition-delay: {getTransitionDelay(2)}"
            title="{artAsset.name}"
          >
            <TextUnderline>
              {truncateString(artAsset.name, 150)}{#if artAsset.creationYear}, {artAsset.creationYear}{/if}
            </TextUnderline>
            {#if artAsset.featured}
              <CuratorBadge classes="inline-block -mb-px" />
            {/if}
          </p>
        {/if}

        <div class="{opacityTransitionClasses}" style="transition-delay: {getTransitionDelay(4)}">
          {#if isActive}
            <ActiveState {isUserBestBidder} bestBid="{normalizedBestBid?.value}" {endDate} {price} />
          {:else if status === AuctionStatus.Sold}
            <SoldState wallet="{normalizedBestBid?.wallet}" />
          {:else if status === AuctionStatus.Finalizing || (status === AuctionStatus.Active && new Date() > endDate)}
            <FinalizeState bestBid="{normalizedBestBid?.value}" {isUserBestBidder} {price} />
          {/if}
        </div>
      </div>

      <div class="{opacityTransitionClasses}" style="transition-delay: {getTransitionDelay(2)}">
        {#if artAsset.type === ArtAssetType.DigitalOriginal}
          <Icon name="do" width="{14}" height="{14}" />
        {:else}
          <Icon name="digital-native" width="{18}" height="{18}" />
        {/if}
      </div>
    </div>
  </div>
</a>
